import { Spacing } from '@persona-id/design-tokens';
import { VFC } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';
import { WrappedFieldInputProps } from 'redux-form';
import styled, { css } from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { ThemeSet } from 'dashboard/models/theme-set';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadStepsPreview } from 'dashboard/redux/thunk-actions/inquiry-template';
import { loadDynamicFlowInquiryTemplateList } from 'dashboard/redux/thunk-actions/organization';
import { loadThemeSets } from 'dashboard/redux/thunk-actions/theme-sets';
import ThemeSetsViewCard from 'dashboard/views/DashboardThemeSetsView/DashboardThemeSetsViewCard';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { Hairline } from 'design-system/variables/effect';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useToggle from 'lib/hooks/useToggle';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { captureMessage } from 'lib/reporting';

const HeaderContainer = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing[4]} ${Spacing[5]};
  border-top: 1px solid ${({ theme }) => theme.color.background.secondary};
  box-shadow: ${Hairline.Bottom};
  margin-bottom: ${Spacing[2]};
`;

const ThemeSetCards = styled('div')`
  display: grid;
  grid-template-columns: repeat(auto-fill, 450px);
  justify-content: space-between;
  grid-gap: 20px;
  > * {
    margin-bottom: 14px;
    height: 250px;
  }
`;

interface ThemeSetCardsModalProps {
  themeSetId: string | null;
  themeSets: ThemeSet[];
  inquiryTemplates: InquiryTemplate[];
  handleClose: () => void;
  selectThemeSet: (themeSetId: string | null) => void;
}

const ThemeSetCardsModal: VFC<ThemeSetCardsModalProps> = ({
  themeSetId,
  themeSets,
  inquiryTemplates,
  handleClose,
  selectThemeSet,
}) => {
  return (
    <ModalContainer id='theme-sets__new-theme-set-modal' size='xl'>
      <ModalHeader onClose={handleClose}>Change default theme</ModalHeader>
      <ModalBody>
        <HeaderContainer>
          <Label size='lg' weight='bold'>
            Themes ({themeSets.length})
          </Label>
          {themeSetId ? (
            <Button
              id='theme-sets__unassign-button'
              variant='basic'
              width='button'
              type='button'
              onClick={() => {
                selectThemeSet(null);
                handleClose();
              }}
            >
              Unassign theme
            </Button>
          ) : null}
        </HeaderContainer>
        <ThemeSetCards>
          {themeSets.map((themeSet: ThemeSet) => (
            <ThemeSetsViewCard
              key={themeSet.id}
              themeSet={themeSet}
              inquiryTemplates={inquiryTemplates}
              onClickThemeSetCard={(themeSet) => {
                selectThemeSet(themeSet.id);
                handleClose();
              }}
            />
          ))}
        </ThemeSetCards>
      </ModalBody>
    </ModalContainer>
  );
};

const getCurrentThemeSet = (
  themeSetId: string | undefined,
  themeSets: ThemeSet[],
): ThemeSet | undefined => {
  if (themeSetId != null) {
    const currentThemeSet = themeSets.find((themeSet) => themeSet.id === themeSetId);
    if (currentThemeSet == null) {
      const themeSetIds = (themeSets || []).map((themeSet) => themeSet.id).join(', ');
      captureMessage(
        `Couldn't find theme set with id: ${themeSetId} from theme sets: ${themeSetIds} in FLE`,
        'warning',
      );
    }
    return currentThemeSet;
  }
};

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ReplaceButton = styled(Button).attrs(() => ({ width: 'auto' }))`
  ${({ theme }) => css`
    background-color: ${theme.color.variants.basic.default};
    color: ${theme.color.variants.basic.text};
  `}
`;

const LoaderContainer = styled.div`
  position: relative;
  width: auto;
  overflow: hidden;
`;

interface FormSectionThemeSetConfigProps {
  input: WrappedFieldInputProps;
}

type ConnectedFormSectionThemeSetConfigProps = FormSectionThemeSetConfigProps & PropsFromRedux;

const ConnectedFormSectionThemeSetConfig = ({
  themeSets,
  orgDefaultThemeSetId,
  inquiryTemplates,
  inquiryTemplateVersion,
  locale,
  input,
  loadThemeSets,
  loadDynamicFlowInquiryTemplateList,
  loadStepsPreview,
}: ConnectedFormSectionThemeSetConfigProps) => {
  const [
    shouldShowThemeSetCardsModal,
    { enable: showThemeSetCardsModal, disable: hideThemeSetCardsModal },
  ] = useToggle(false);

  const { loading: isLoading } = useApiLoader({
    loader: async () => {
      await Promise.all([
        loadThemeSets({
          omni: undefined,
          sortParams: { sort: 'updated_at', shouldSortDescending: true },
        }),
        loadDynamicFlowInquiryTemplateList(),
      ]);
    },
    deps: [loadThemeSets],
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load themes.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  // we need to reload the Steps preview after a theme set change
  const { callback: handleLoadStepsPreview, error: handleLoadStepsPreviewError } =
    useApiCallback(async () => {
      await loadStepsPreview(inquiryTemplateVersion.id, locale);
    }, []);

  useApiErrorHandler({
    error: handleLoadStepsPreviewError,
    onError: (err) => {
      if (isPermissionsError(err)) {
        showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        showMessage('Failed to update template preview, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const themeSetId = input.value || orgDefaultThemeSetId;
  const isOrgDefaultThemeSet = orgDefaultThemeSetId && themeSetId === orgDefaultThemeSetId;

  const setThemeSet = (themeSetId: string | null) => {
    input.onChange(themeSetId);
    handleLoadStepsPreview();
  };

  const currentThemeSet = getCurrentThemeSet(themeSetId, themeSets);

  return (
    <>
      <ModalWrapper isOpen={shouldShowThemeSetCardsModal} handleCloseModal={hideThemeSetCardsModal}>
        <ThemeSetCardsModal
          themeSetId={themeSetId}
          themeSets={themeSets}
          inquiryTemplates={inquiryTemplates}
          handleClose={hideThemeSetCardsModal}
          selectThemeSet={setThemeSet}
        />
      </ModalWrapper>
      <Card key='default-theme-set'>
        <Card.Body>
          <TitleContainer>
            <Card.Title>
              <div>
                Default theme
                {isOrgDefaultThemeSet ? ' (Organization default theme)' : null}
              </div>
            </Card.Title>
            <ReplaceButton
              variant='basic'
              id='theme-sets-config__open-theme-set-cards-modal'
              onClick={showThemeSetCardsModal}
              disabled={themeSets.length === 0}
              type='button'
            >
              {themeSetId ? 'Replace' : 'Select'}
            </ReplaceButton>
          </TitleContainer>
          {isLoading ? (
            <LoaderContainer>
              <LoadingIndicator />
            </LoaderContainer>
          ) : currentThemeSet ? (
            <ThemeSetsViewCard
              themeSet={currentThemeSet}
              inquiryTemplates={inquiryTemplates}
              vertical={false}
            />
          ) : (
            <Paragraph>
              {themeSetId
                ? 'Unable to load the default theme set for this template, please try refreshing the page'
                : 'No template default theme selected yet'}
            </Paragraph>
          )}
          <Spacer height={0.5} />
          <Link to='/dashboard/theme-sets'> Manage your themes</Link>
        </Card.Body>
      </Card>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    themeSets: state.themeSets.themeSets,
    orgDefaultThemeSetId: state.organization.organization?.relationships['theme-set'].data?.id,
    inquiryTemplates: state.organization.inquiryTemplates,
    inquiryTemplateVersion: state.inquiryTemplate.inquiryTemplateTreeData.inquiryTemplateVersion,
    locale: state.inquiryTemplate.locale,
  };
};

const mapDispatchToProps = {
  loadThemeSets,
  showMessage,
  loadDynamicFlowInquiryTemplateList,
  loadStepsPreview,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(ConnectedFormSectionThemeSetConfig);
